import React, { useEffect, useMemo, useState } from "react";
import { Swords, Trash2 } from "lucide-react";
import {
  acceptP2PInviteWithAck,
  cancelP2PBetWithAck,
  rejectP2PInviteWithAck,
} from "../../redux/p2pSocket";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import { formatFixtureDate, formatFixtureTime } from "../../utils/formatFixtureDate";
import {
  calculateTimeRemaining,
  formatOutcome,
  getRemainingCounterCapacity,
  getCreatorBudget,
  getOfferedOdds,
  counterPayout,
  getUserStake,
  MIN_COUNTER_STAKE,
  getMinCounterStake,
} from "../../utils/p2pHelpers";
import {
  formatInviteStatus,
  inviteStatusTone,
  isInvitePending,
  p2pCardFillPercent,
  p2pCardJoinedCount,
} from "../../utils/requestsHelpers";
import CancelP2PModal from "../p2p/CancelP2PModal";
import JoinP2PModal from "../p2p/JoinP2PModal";
import RequestConfirmDialog from "./RequestConfirmDialog";

const TeamInline = ({ team }) => {
  const [failed, setFailed] = useState(false);
  const initial = (team?.name?.[0] || "?").toUpperCase();

  return (
    <div className="req-invite-team">
      {team?.logo && !failed ? (
        <img src={team.logo} alt="" onError={() => setFailed(true)} />
      ) : (
        <span>{initial}</span>
      )}
      <em>{team?.name || "TBD"}</em>
    </div>
  );
};

const Fact = ({ label, value, accent = false }) => (
  <div className={`req-invite-fact ${accent ? "req-invite-fact-accent" : ""}`}>
    <span>{label}</span>
    <strong className="tabular-nums" title={typeof value === "string" ? value : undefined}>
      {value}
    </strong>
  </div>
);

const RequestInviteCard = ({ item, userId, onNotify, onRefresh, onResolved }) => {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localP2PStatus, setLocalP2PStatus] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);

  const payload = item?.payload;
  const isP2P = payload?.type === "p2p";
  const direction = payload?.direction;
  const isReceived = direction === "received";

  const p2pBet = isP2P ? payload.bet : null;
  const fixture = p2pBet?.fixture || {};

  useEffect(() => {
    setLocalP2PStatus(null);
    setConfirm(null);
    setBusy(false);
    setCancelOpen(false);
    setJoinModalOpen(false);
  }, [item?.id]);

  useEffect(() => {
    if (!fixture?.date) {
      setCountdown("");
      return undefined;
    }
    const tick = () => setCountdown(calculateTimeRemaining(fixture.date));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [fixture?.date]);

  const p2pDetails = useMemo(() => {
    if (!p2pBet) return null;
    const invite =
      direction === "received"
        ? p2pBet.invites?.find((i) => {
            const uid = i.user?._id ?? i.user;
            return uid && String(uid) === String(userId);
          })
        : null;
    const inviteStatus = localP2PStatus ?? invite?.status ?? "";
    const joined = p2pCardJoinedCount(p2pBet, direction);
    const remainingCap = getRemainingCounterCapacity(p2pBet);
    const creatorBudget = getCreatorBudget(p2pBet);
    const fillPercent = p2pCardFillPercent(p2pBet);
    const amount = Number(p2pBet?.amount || 0);
    const odds = getOfferedOdds(p2pBet);
    return {
      inviteStatus,
      statusLabel: formatInviteStatus(inviteStatus),
      tone: inviteStatusTone(inviteStatus),
      pending: isInvitePending(inviteStatus),
      accepted: String(inviteStatus).toLowerCase() === "accepted",
      rejected: String(inviteStatus).toLowerCase() === "rejected",
      outcome: formatOutcome(p2pBet?.agreedOdds, fixture),
      amount,
      joined,
      remainingCap,
      creatorBudget,
      fillPercent,
      potentialWin: counterPayout(amount, odds),
      timeLabel: countdown || formatFixtureTime(fixture?.date),
      dateLabel: formatFixtureDate(fixture?.date),
    };
  }, [p2pBet, direction, userId, localP2PStatus, fixture, countdown]);

  const refreshAll = async () => {
    await onRefresh?.();
  };

  const runP2PAction = async (kind, { stake } = {}) => {
    if (!userId || !p2pBet?._id || busy) return;

    setBusy(true);
    try {
      if (kind === "accept") {
        const stakeAmount = Math.max(MIN_COUNTER_STAKE,
  getMinCounterStake, Number(stake) || MIN_COUNTER_STAKE);
        const userStake = getUserStake(p2pBet, userId);
        const isAddStake = userStake > 0 || p2pDetails?.accepted;
        const payload = {
          userId,
          p2pId: p2pBet._id,
          stake: stakeAmount,
        };
        if (isAddStake) {
          payload.idempotencyKey = `add-${p2pBet._id}-${userId}-${stakeAmount}-${Date.now()}`;
        }
        await acceptP2PInviteWithAck(payload);
        setLocalP2PStatus("Accepted");
        setJoinModalOpen(false);
        onNotify?.(
          "success",
          "Done",
          isAddStake ? "Stake added." : "P2P request accepted."
        );
      } else {
        await rejectP2PInviteWithAck({ userId, p2pId: p2pBet._id });
        setLocalP2PStatus("Rejected");
        onNotify?.("success", "Done", "P2P request declined.");
      }
      setConfirm(null);
      await refreshAll();
      onResolved?.(item.id);
    } catch (error) {
      onNotify?.("error", "Request failed", error?.message || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleP2PCancel = async () => {
    if (!userId || !p2pBet?._id || busy) return;
    setBusy(true);
    try {
      const response = await cancelP2PBetWithAck({ userId, p2pId: p2pBet._id });
      setCancelOpen(false);
      onNotify?.("success", "Done", response?.message || "P2P bet canceled.");
      await refreshAll();
      onResolved?.(item.id);
    } catch (error) {
      onNotify?.("error", "Request failed", error?.message || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  const getConfirmConfig = () => {
    if (!confirm) return null;

    if (confirm === "reject-p2p") {
      return {
        title: "Decline P2P request?",
        body: "The creator will be notified that you passed on this private request.",
        confirmLabel: "Decline request",
        destructive: true,
        onConfirm: () => runP2PAction("reject"),
      };
    }

    return null;
  };

  const confirmConfig = getConfirmConfig();

  const showP2PSentActions = isP2P && direction === "sent";
  const showP2PReceivedPending =
    isP2P && direction === "received" && p2pDetails?.pending;
  const showP2PReceivedAddSpots =
    isP2P &&
    direction === "received" &&
    p2pDetails?.accepted &&
    getRemainingCounterCapacity(p2pBet) >= getMinCounterStake(p2pBet) &&
    p2pBet?.status === "Active";
  const hasActions =
    showP2PSentActions || showP2PReceivedPending || showP2PReceivedAddSpots;

  const statusTone = p2pDetails?.tone || item.statusTone;
  const statusLabel = p2pDetails?.statusLabel || item.statusLabel;

  const renderP2PActions = () =>
    hasActions && isP2P ? (
      <div className="req-invite-row-actions">
        {showP2PSentActions ? (
          <>
            <button
              type="button"
              className="req-btn req-btn-ghost req-btn-icon"
              disabled={busy}
              onClick={() => setCancelOpen(true)}
              aria-label="Cancel P2P bet"
            >
              <Trash2 size={12} />
            </button>
          </>
        ) : null}
        {showP2PReceivedPending ? (
          <>
            <button
              type="button"
              className="req-btn req-btn-ghost"
              disabled={busy}
              onClick={() => setConfirm("reject-p2p")}
            >
              Decline
            </button>
            <button
              type="button"
              className="req-btn req-btn-success"
              disabled={busy || getRemainingCounterCapacity(p2pBet) < getMinCounterStake(p2pBet)}
              onClick={() => setJoinModalOpen(true)}
            >
              Join
            </button>
          </>
        ) : null}
        {showP2PReceivedAddSpots ? (
          <button
            type="button"
            className="req-btn req-btn-primary"
            disabled={busy}
            onClick={() => setJoinModalOpen(true)}
          >
            Add stake
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <>
      <article
        id={`request-${item.id}`}
        className={`req-invite-card req-invite-card-p2p ${
          item.needsReply ? "req-invite-card-action" : ""
        } ${!item.needsReply && !item.awaitingOthers ? "req-invite-card-done" : ""}`}
      >
        {isP2P && p2pBet && p2pDetails ? (
          <div className="req-invite-row req-invite-row-p2p">
            <div className="req-p2p-crest" aria-hidden>
              <Swords size={15} strokeWidth={2.2} />
              <span className="req-p2p-crest-dir">{isReceived ? "In" : "Out"}</span>
            </div>

            <div className="req-p2p-match">
              <TeamInline team={fixture?.teams?.home} />
              <span className="req-p2p-fixture-status">
                {fixture?.status?.short || "—"}
              </span>
              <TeamInline team={fixture?.teams?.away} />
            </div>

            <div className="req-p2p-facts">
              <Fact label="Budget" value={formatCash(p2pDetails.creatorBudget)} />
              <Fact label="Min stake" value={formatCash(getMinCounterStake(p2pDetails))} />
              {isReceived ? (
                <Fact label="Their pick" value={p2pDetails.outcome} accent />
              ) : null}
              <Fact
                label="Capacity"
                value={`${p2pDetails.joined} counter${p2pDetails.joined === 1 ? "" : "s"} · Cash ${p2pDetails.remainingCap?.toLocaleString?.() ?? 0} left`}
              />
              <Fact label="Kickoff" value={p2pDetails.timeLabel || "—"} />
              {isReceived && p2pDetails.pending ? (
                <Fact label="Win up to" value={formatCash(p2pDetails.potentialWin)} />
              ) : null}
            </div>

            <div className="req-p2p-side">
              {direction === "received" && !p2pDetails.pending ? (
                <span className={`req-p2p-status req-p2p-status-${statusTone}`}>
                  {statusLabel}
                </span>
              ) : null}
              {direction === "sent" ? (
                <div
                  className="req-p2p-fill"
                  aria-label={`${p2pDetails.fillPercent}% of budget matched`}
                >
                  <span className="req-p2p-fill-bar">
                    <i style={{ width: `${p2pDetails.fillPercent}%` }} />
                  </span>
                  <em className="tabular-nums">{p2pDetails.fillPercent}% full</em>
                </div>
              ) : null}
              {renderP2PActions()}
            </div>
          </div>
        ) : null}
      </article>

      <RequestConfirmDialog
        open={Boolean(confirmConfig)}
        busy={busy}
        onClose={() => {
          if (!busy) setConfirm(null);
        }}
        {...(confirmConfig || {})}
      />

      <JoinP2PModal
        open={joinModalOpen}
        onClose={() => {
          if (!busy) setJoinModalOpen(false);
        }}
        onConfirm={({ stake } = {}) => runP2PAction("accept", { stake })}
        bet={p2pBet}
        loading={busy}
        mode={p2pDetails?.accepted ? "add-stake" : "invite"}
        userId={userId}
      />

      <CancelP2PModal
        open={cancelOpen}
        onClose={() => {
          if (!busy) setCancelOpen(false);
        }}
        onConfirm={handleP2PCancel}
        loading={busy}
      />
    </>
  );
};

export default RequestInviteCard;
